import { useAuth } from "../lib/AuthContext";
import { config } from "../lib/config";
import { Alert, AlertDescription } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Code, Database, User, Building2 } from "lucide-react";
import { useFeature } from "../lib/FeatureFlagsContext";

/**
 * Debug Banner Component
 * Displays environment, auth.uid, and org info when in non-production
 */

export function DebugBanner() {
  const { user, profile } = useAuth();
  const isDebugEnabled = useFeature('debugBanner');
  
  // Only show in non-production environments
  const isProduction = config.environment === 'production';
  
  // Don't render in production or if disabled
  if (isProduction || !isDebugEnabled) {
    return null;
  }

  const getEnvironmentColor = () => {
    switch (config.environment) {
      case 'development':
        return 'bg-blue-500/10 border-blue-500/20 text-blue-700 dark:text-blue-300';
      case 'staging':
        return 'bg-amber-500/10 border-amber-500/20 text-amber-700 dark:text-amber-300';
      case 'test':
        return 'bg-purple-500/10 border-purple-500/20 text-purple-700 dark:text-purple-300';
      default:
        return 'bg-muted';
    }
  };

  return (
    <Alert className={`border-2 ${getEnvironmentColor()} rounded-none`}>
      <Code className="w-4 h-4" />
      <AlertDescription>
        <div className="flex items-center gap-4 flex-wrap text-sm">
          {/* Environment */}
          <div className="flex items-center gap-2">
            <span className="font-semibold">ENV:</span>
            <Badge variant="outline" className="uppercase">
              {config.environment}
            </Badge>
          </div>

          {/* Auth UID */}
          {user && (
            <div className="flex items-center gap-2">
              <User className="w-3.5 h-3.5" />
              <span className="font-semibold">UID:</span>
              <code className="font-mono text-xs bg-muted px-2 py-0.5 rounded">
                {user.id.substring(0, 8)}...{user.id.substring(user.id.length - 4)}
              </code>
            </div>
          )}

          {/* Role */}
          {profile && (
            <div className="flex items-center gap-2">
              <Database className="w-3.5 h-3.5" />
              <span className="font-semibold">Role:</span>
              <Badge variant="secondary" className="uppercase">
                {profile.role}
              </Badge>
            </div>
          )}

          {/* Organisation (Clinic ID) */}
          {profile?.clinic_id && (
            <div className="flex items-center gap-2">
              <Building2 className="w-3.5 h-3.5" />
              <span className="font-semibold">Org:</span>
              <code className="font-mono text-xs bg-muted px-2 py-0.5 rounded">
                {profile.clinic_id.substring(0, 8)}...
              </code>
            </div>
          )}

          {/* Dev Mode Indicator */}
          {config.devMode.enabled && (
            <Badge variant="default" className="bg-primary">
              🔧 DEV MODE
            </Badge>
          )}

          {/* Mock Auth Indicator */}
          {config.devMode.mockAuth && (
            <Badge variant="outline" className="border-amber-500 text-amber-700 dark:text-amber-300">
              🎭 MOCK AUTH
            </Badge>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
}
